import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BottomNavigation from "@/components/BottomNavigation";
import ProjectForm from "@/components/project/ProjectForm";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { projectsAPI } from "@/api/projects";
import { useAuth } from "@/contexts/AuthContext";
import { ScreenHeader } from "@/components/redesign";

const EditProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const updateAttempted = useRef(false);

  // Fetch project data on component mount
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!id || fetchAttempted) return;

      setIsLoading(true);
      setError(null);
      setFetchAttempted(true);

      try {
        const projectData = await projectsAPI.getProject(id);
        if (!projectData) {
          throw new Error(t('projects.project_not_found'));
        }


        setProject(projectData);
      } catch (err) {

        setError(t('projects.error_loading_project'));
        toast({
          title: t('common.error'),
          description: t('projects.error_loading_project'),
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [id, toast, fetchAttempted]);

  // Format project data for the form
  const getInitialFormData = () => {
    if (!project) {
      return null;
    }

    try {

      // Extract unit areas for flats and shops
      const flatAreas: number[] = [];
      const shopAreas: number[] = [];

      const details = project.details || {};
      const units = details.units || [];

      // Process units if they exist
      if (Array.isArray(units)) {
        units.forEach(unit => {
          if (!unit || !unit.type || !unit.number || unit.area === undefined) return;

          if (unit.type === 'flat' && unit.number) {
            // Make sure the array is big enough
            while (flatAreas.length < unit.number) {
              flatAreas.push(0);
            }
            // Set the area at the correct index (number - 1 because array is 0-indexed)
            flatAreas[unit.number - 1] = unit.area;
          } else if (unit.type === 'shop' && unit.number) {
            while (shopAreas.length < unit.number) {
              shopAreas.push(0);
            }
            shopAreas[unit.number - 1] = unit.area;
          }
        });
      }

      const formData = {
        id: project.id,
        name: project.name || "",
        status: project.status || "active",
        parcel: details.parcel || "",
        lot: details.lot || "",
        flats: details.flats || 0,
        shops: details.shops || 0,
        area: details.area || 0,
        cost_tl: project.cost_try || project.budget || 0, // Try cost_try first, then budget as fallback
        description: project.description || "",
        flatAreas,
        shopAreas
      };

      return formData;
    } catch (error) {
      setError(t('projects.error_processing_project_data'));

      // Return a minimal form data structure instead of null to prevent blank form
      return {
        id: project.id || "",
        name: project.name || "",
        status: project.status || "active",
        parcel: "",
        lot: "",
        flats: 0,
        shops: 0,
        area: 0,
        cost_tl: project.cost_try || project.budget || 0,
        description: project.description || "",
        flatAreas: [],
        shopAreas: []
      };
    }
  };

  const handleProjectSubmit = async (data: any) => {
    // Prevent multiple update attempts
    if (updateAttempted.current) {

      return;
    }

    // Mark that we've attempted an update
    updateAttempted.current = true;




    // Calculate total area from unit areas if not already provided
    if (!data.area && (data.flatAreas || data.shopAreas)) {
      const flatAreasTotal = (data.flatAreas || []).reduce((sum: number, area: number) => sum + (area || 0), 0);
      const shopAreasTotal = (data.shopAreas || []).reduce((sum: number, area: number) => sum + (area || 0), 0);
      data.area = flatAreasTotal + shopAreasTotal;
    }

    // Prepare unit details for storage
    const units = [];

    if (data.flatAreas && data.flatAreas.length > 0) {
      data.flatAreas.forEach((area: number, index: number) => {
        if (area) {
          units.push({
            type: 'flat',
            number: index + 1,
            area: area
          });
        }
      });
    }

    if (data.shopAreas && data.shopAreas.length > 0) {
      data.shopAreas.forEach((area: number, index: number) => {
        if (area) {
          units.push({
            type: 'shop',
            number: index + 1,
            area: area
          });
        }
      });
    }



    try {
      // Only proceed if we have a user and project id
      if (!user) {
        toast({
          title: t('common.error'),
          description: t('projects.session_not_active'),
          variant: "destructive"
        });
        navigate("/login");
        return;
      }

      if (!id) {
        toast({
          title: t('common.error'),
          description: t('projects.project_id_not_found'),
          variant: "destructive"
        });
        return;
      }

      // Prepare the data object for updating the project
      const updateData = {
        name: data.name,
        status: data.status,
        budget: data.cost_tl, // Map cost_tl to budget
        details: {
          parcel: data.parcel || "",
          lot: data.lot || "",
          flats: data.flats || 0,
          shops: data.shops || 0,
          area: data.area || 0,
          units: units
        },
        description: data.description || ""
      };



      // Update the project in Supabase through API

      const result = await projectsAPI.updateProject(id, updateData);


      // Show success toast
      toast({
        title: t('projects.project_updated_successfully'),
        description: t('projects.project_saved_successfully'),
      });

      // Dispatch a custom event to notify listeners that project data has been updated
      window.dispatchEvent(new CustomEvent('projectDataChanged', {
        detail: { projectId: id }
      }));

      // Navigate back to project detail page

      navigate(`/project/${id}`);
    } catch (error) {

      // Reset update attempted flag so user can try again
      updateAttempted.current = false;
      toast({
        title: t('projects.project_update_failed'),
        description: t('projects.project_update_error'),
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container bg-gray-25">
        <ScreenHeader title={t('projects.editing_project')} />

        <div className="main-content flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-brand-600" />
            <p className="text-lg font-medium text-gray-700">{t('projects.loading_project')}</p>
          </div>
        </div>

        <BottomNavigation />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="page-container bg-gray-25">
        <ScreenHeader title={t('common.error')} />

        <div className="main-content bg-gray-25 px-4 pt-4">
          <div className="bg-white border border-error-600/20 rounded-2xl p-6 text-center shadow-sm">
            <p className="text-error-600 mb-4">{error || t('projects.project_not_found')}</p>
            <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => navigate(-1)}>{t('projects.go_back')}</Button>
          </div>
        </div>

        <BottomNavigation />
      </div>
    );
  }

  const initialData = getInitialFormData();

  return (
    <div className="page-container bg-gray-25">
      <ScreenHeader title={t('projects.edit_project')} />

      <div className="main-content pb-20 bg-gray-25">
        <div className="w-full max-w-3xl mx-auto pt-4">
          {initialData ? (
            <ProjectForm
              onSubmit={handleProjectSubmit}
              initialData={initialData}
              submitButtonText={t('projects.update_project')}
            />
          ) : (
            <div className="bg-white border border-error-600/20 rounded-2xl p-6 text-center shadow-sm">
              <p className="text-error-600 mb-4">
                {error || t('projects.error_loading_project_data')}
              </p>
              <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => navigate(-1)}>
                {t('common.back')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default EditProjectPage;
